// =============================================================================
// #1900 — Routines: the parts of her week that repeat.
//
// ⚠️ THIS LIVES IN `_shared/` ON PURPOSE, and it is the whole reason the module
// exists separately from the component.
//
// Two surfaces have to answer "what does this routine say today?" and they must
// never disagree:
//   • the Routines tool, in her browser (PR 1) — the card subline, "Up next",
//     and the Skip button.
//   • dispatch-notifications, in Deno (PR 2) — the sentence in her morning note
//     and the cursor advance.
//
// #1960 is the cautionary tale: MorningNotesCard built a due date with
// `new Date("...T18:00:00")`, correct in a browser and five hours wrong in the
// UTC edge runtime, and a server-side copy of that expression would have
// shipped the defect. The fix was to share the function, not to re-derive it.
// Doing that FIRST here costs nothing; doing it later costs a bug she feels.
//
// src/ imports this by relative path — `../../supabase/functions/_shared/...` —
// which is the established direction of travel (ChatMessages, MyKinTab,
// PersonalBudget, TasksTab and calendarRangeChunks all do it today).
//
// ⚠️ NOTHING HERE TOUCHES THE NETWORK OR THE CLOCK unless a date is passed in.
// Every function is pure, so both callers can be tested without a database and
// neither can drift from the other.
// =============================================================================

export type RoutineMode = "weekday" | "rotation";

/** A `routines` row, as both sides read it. */
export interface RoutineRow {
  id: string;
  name: string;
  emoji?: string | null;
  mode: string;
  items: unknown;
  cursor: number;
  last_advanced_on?: string | null;
  in_morning_note: boolean;
  is_active: boolean;
  sort_order?: number | null;
}

/** One weekday line. 120 characters, per the spec and the editor's maxLength. */
export const ROUTINE_LINE_MAX = 120;

/** Longest routine NAME. Keeps the card subline and the morning sentence sane. */
export const ROUTINE_NAME_MAX = 40;

/**
 * ⚠️ 0 = SUNDAY … 6 = Saturday — JavaScript `getDay()`, and the same convention
 * `repeat_days` uses in #1873. NOT Postgres `isodow` (1 = Monday … 7 = Sunday).
 * An off-by-one between these two is invisible on five days out of seven, which
 * is exactly long enough to ship.
 */
export const WEEKDAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"] as const;

/** Display order is Monday-first, which is how a week reads. Storage is not. */
export const WEEKDAY_DISPLAY_ORDER: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

export const WEEKDAY_SHORT_LABELS: Readonly<Record<number, string>> = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

/** The five days "Copy to all weekdays" fills. Saturday and Sunday are hers. */
export const WEEKDAY_WORKWEEK: readonly number[] = [1, 2, 3, 4, 5];

/** What the editor shows in an empty weekday box. Blank is a real answer. */
export const WEEKDAY_BLANK_PLACEHOLDER = "Nothing, Kin stays quiet";

export function isRoutineMode(raw: unknown): raw is RoutineMode {
  return raw === "weekday" || raw === "rotation";
}

/** A mode we can act on, or null. An unknown mode is never guessed at. */
export function normalizeMode(raw: unknown): RoutineMode | null {
  return isRoutineMode(raw) ? raw : null;
}

/**
 * Trim and cap one line of routine prose.
 *
 * ⚠️ Returns "" for anything that is not a usable string, so a null, a number
 * or an object out of jsonb all collapse to "blank", which the readers already
 * treat as "Kin says nothing". No caller has to repeat that check.
 */
export function cleanLine(raw: unknown, max: number = ROUTINE_LINE_MAX): string {
  if (typeof raw !== "string") return "";
  return raw.trim().slice(0, max);
}

/**
 * `items` for a WEEKDAY routine, normalised to `{ "0".."6": string }`.
 *
 * ⚠️ An array in here is not an error to throw on. A row inserted on the
 * table's `'[]'` default holds one until its first save, so the honest reading
 * of a wrong-shaped `items` is "nothing scheduled yet" — a routine that says
 * nothing, not a tool that will not open.
 *
 * Missing and blank days are both omitted, so the result's keys are exactly the
 * days Kin has something to say on.
 */
export function weekdayItems(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  const source = raw as Record<string, unknown>;
  for (const key of WEEKDAY_KEYS) {
    const line = cleanLine(source[key]);
    if (line) out[key] = line;
  }
  return out;
}

/**
 * `items` for a ROTATION routine, normalised to a list of non-empty strings.
 *
 * Blank entries are dropped rather than kept as empty turns: "one per morning"
 * means a morning where Kin says nothing is a bug, not a turn.
 */
export function rotationItems(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const entry of raw) {
    const line = cleanLine(entry);
    if (line) out.push(line);
  }
  return out;
}

/**
 * Fold any stored cursor into range.
 *
 * ⚠️ Handles NEGATIVE and out-of-range values, not just the wrap. A plain
 * `cursor % len` returns a negative index for a negative cursor, and
 * `items[-1]` is `undefined`, which would show a mom a blank turn with nothing
 * in the UI to explain it. Removing an item from the list is the ordinary way
 * the stored cursor ends up past the end.
 */
export function safeCursor(cursor: unknown, length: number): number {
  if (!Number.isFinite(length) || length <= 0) return 0;
  const n = typeof cursor === "number" && Number.isFinite(cursor) ? Math.trunc(cursor) : 0;
  return ((n % length) + length) % length;
}

/** `Date` → "YYYY-MM-DD" in the LOCAL fields of that date. Never UTC. */
export function localYmd(date: Date): string | null {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = String(date.getFullYear()).padStart(4, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The weekday of a "YYYY-MM-DD" string, 0 = Sunday.
 *
 * ⚠️ ROUND-TRIPPED. `Date.UTC` normalises impossible dates — Feb 30 becomes
 * Mar 2, and Mar 2 really is some weekday, so the wrong answer looks exactly
 * like a right one. #1960 shipped `weekdayOf("2026-02-30") === "Monday"` into a
 * test before this check caught it.
 */
export function weekdayOfYmd(ymd: unknown): number | null {
  if (typeof ymd !== "string") return null;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const utc = Date.UTC(year, month - 1, day);
  const back = new Date(utc);
  if (
    back.getUTCFullYear() !== year ||
    back.getUTCMonth() !== month - 1 ||
    back.getUTCDate() !== day
  ) {
    return null;
  }
  return back.getUTCDay();
}

/**
 * What this routine says on the given weekday — or null for "nothing today".
 *
 * ⚠️ null is a FIRST-CLASS answer, not a failure. A blank Saturday means Kin
 * stays quiet, and the morning note must leave the sentence out entirely rather
 * than say "nothing today", which is noise about an absence she chose.
 */
export function routineLineFor(routine: RoutineRow, weekday: number): string | null {
  const mode = normalizeMode(routine?.mode);
  if (!mode) return null;

  if (mode === "weekday") {
    if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) return null;
    return weekdayItems(routine.items)[String(weekday)] ?? null;
  }

  const list = rotationItems(routine.items);
  if (list.length === 0) return null;
  return list[safeCursor(routine.cursor, list.length)] ?? null;
}

/** The rotation item up next, for the editor's "Up next: floors". */
export function rotationUpNext(routine: RoutineRow): string | null {
  if (normalizeMode(routine?.mode) !== "rotation") return null;
  const list = rotationItems(routine.items);
  if (list.length === 0) return null;
  return list[safeCursor(routine.cursor, list.length)] ?? null;
}

export interface RotationAdvance {
  cursor: number;
  last_advanced_on: string;
}

/**
 * The cursor move for one morning, or null when it must NOT move.
 *
 * Refuses when:
 *   • the routine is not a rotation (a weekday routine has no cursor)
 *   • the list is empty (nothing to advance through)
 *   • `last_advanced_on` is already today — ⚠️ THE SAME-DAY GUARD. The morning
 *     pass can be retried, and an unguarded advance would skip a turn every
 *     time it was. Skipping is the failure she would notice and could not
 *     explain; repeating one is the failure that fixes itself tomorrow, so the
 *     guard fails toward repeating.
 *
 * ⚠️ Compares DATE STRINGS, not instants. "Today" for a routine is her local
 * calendar day, and the caller is the one that knows her zone.
 */
export function rotationAdvance(routine: RoutineRow, todayYmd: string): RotationAdvance | null {
  if (normalizeMode(routine?.mode) !== "rotation") return null;
  if (typeof todayYmd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(todayYmd)) return null;
  if (routine.last_advanced_on === todayYmd) return null;

  const list = rotationItems(routine.items);
  if (list.length === 0) return null;

  const current = safeCursor(routine.cursor, list.length);
  return { cursor: (current + 1) % list.length, last_advanced_on: todayYmd };
}

/**
 * Move one rotation item from one index to another, returning a NEW list.
 *
 * ⚠️ Lives here rather than inside the editor's drag handler, and that is the
 * whole reason reorder has any test at all. Kasandra's call (2026-11-01) was
 * the mockup exactly: a drag handle and nothing else, no arrow buttons. A
 * pointer gesture cannot be driven reliably in Playwright, so if the reorder
 * logic also lived inside the handler it would ship with no coverage of any
 * kind. Split this way, the GESTURE is untested and the BEHAVIOUR is not.
 *
 * Out-of-range and no-op moves return the list unchanged rather than throwing
 * or producing an undefined entry: a drop outside the list is an ordinary
 * thing a thumb does.
 */
export function moveRotationItem(list: readonly string[], from: number, to: number): string[] {
  const out = Array.isArray(list) ? [...list] : [];
  if (!Number.isInteger(from) || !Number.isInteger(to)) return out;
  if (from === to) return out;
  if (from < 0 || to < 0 || from >= out.length || to >= out.length) return out;
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved);
  return out;
}

/**
 * The cursor move for a HAND tap on "Skip", or null if there is nothing to skip.
 *
 * ⚠️ DELIBERATELY NOT `rotationAdvance`, and this is the trap worth naming.
 * Skip must IGNORE the same-day guard: on any morning Kin has already sent the
 * note, `last_advanced_on` is today, so routing Skip through `rotationAdvance`
 * would return null and the button would do nothing at all — silently, because
 * null is also the ordinary "nothing to advance" answer. Mornings are exactly
 * when she is looking at the card and most likely to tap it.
 *
 * ⚠️ It does NOT stamp `last_advanced_on` either. If she skips before the
 * morning pass runs, the note shows the item she skipped TO and the dispatcher
 * then advances for tomorrow. Two moves in one day is correct there: she asked
 * for one of them.
 */
export function manualSkipCursor(routine: RoutineRow): number | null {
  if (normalizeMode(routine?.mode) !== "rotation") return null;
  const list = rotationItems(routine.items);
  if (list.length === 0) return null;
  return (safeCursor(routine.cursor, list.length) + 1) % list.length;
}

/**
 * The subline under a routine's name on its card.
 *
 * ⚠️ It always says what Kin WOULD say today, so she can sanity-check a routine
 * without opening it — that is the mockup's stated job for this line. A paused
 * routine says it is paused instead of showing a line it is not going to use.
 */
export function routineSubline(routine: RoutineRow, weekday: number): string {
  const mode = normalizeMode(routine?.mode);
  const modeLabel = mode === "rotation" ? "Takes turns" : "By weekday";

  if (routine?.is_active === false) return `${modeLabel} · Paused`;

  if (mode === "rotation") {
    const next = rotationUpNext(routine);
    return next ? `${modeLabel} · Up next: ${next}` : `${modeLabel} · Nothing in the list yet`;
  }

  const line = routineLineFor(routine, weekday);
  return line ? `${modeLabel} · Today: ${line}` : `${modeLabel} · Nothing today`;
}

/**
 * Is this routine in a state worth sending to the morning note?
 *
 * Both flags AND something to actually say. Shared so PR 2's dispatcher asks
 * the same question the tool answers on the card.
 */
export function routineSpeaksToday(routine: RoutineRow, weekday: number): boolean {
  if (!routine?.is_active || !routine?.in_morning_note) return false;
  return routineLineFor(routine, weekday) !== null;
}

// ── Starter routines (empty state) ──────────────────────────────────────────
// Prefill only. ⚠️ NOTHING IS SAVED until she taps Save — tapping a starter
// opens the editor filled in, which is the same promise the chat hand-off makes
// in PR 2, so the two entry points behave identically.

export interface RoutineStarter {
  key: string;
  emoji: string;
  name: string;
  mode: RoutineMode;
  items: Record<string, string> | string[];
}

export const ROUTINE_STARTERS: readonly RoutineStarter[] = [
  {
    key: "homeschool",
    emoji: "📚",
    name: "Homeschool week",
    mode: "weekday",
    items: {
      "1": "Math, science. Library run.",
      "2": "Reading, art.",
      "3": "Math, science. Co-op at 1.",
      "4": "Reading, history.",
      "5": "Nature walk, catch-up.",
    },
  },
  {
    key: "chores",
    emoji: "🧹",
    name: "House chores",
    mode: "rotation",
    items: ["Bathrooms", "Floors", "Laundry", "Fridge clean-out"],
  },
  // ⚠️ DELIBERATE, and the issue says so: this tool is for her, not only for
  // the house. Two starters about work and one about rest is the point.
  {
    key: "metime",
    emoji: "💆‍♀️",
    name: "Me time",
    mode: "rotation",
    items: ["20 minutes with a book", "Call a friend", "Long shower", "Sit outside with coffee"],
  },
];

export const ROUTINE_STARTERS_HEADING = "Start with one of these, or make your own.";

// =============================================================================
// #1900 PR 3 — the chat hand-off.
//
// kin-chat can offer to set a rhythm up as a routine. On yes it ends the reply
// with [OPEN_ROUTINES] and a hidden [ROUTINE_DRAFT]{...}[/ROUTINE_DRAFT] block,
// and the app opens the Routines editor PREFILLED from it. Nothing is written
// until she taps Save.
//
// ⚠️ THE PARSER LIVES HERE, NOT IN THE CLIENT, because three surfaces need the
// same answer: Index.tsx prefills from it, ChatMessages.tsx strips it from what
// it renders, and the SMS path in _shared/stripModelTags.ts strips it from what
// it texts. A second copy of the regex is how one of those three ends up
// showing her raw JSON.
// =============================================================================

/**
 * The hidden draft block.
 *
 * ⚠️ NON-GREEDY BODY (`[\s\S]*?`), so two blocks in one reply cannot be
 * swallowed into one match with everything between them. Shaped like
 * ChatMessages.tsx's TODO_LIST_RE, which is the precedent for a paired block
 * whose body is machine payload.
 */
export const ROUTINE_DRAFT_RE = /\[ROUTINE_DRAFT\]([\s\S]*?)\[\/ROUTINE_DRAFT\]/i;

/** Openers and closers on their own, for the orphan case a cut stream leaves. */
export const ROUTINE_DRAFT_MARKER_RE = /\[\/?ROUTINE_DRAFT\]/gi;

/**
 * How many rotation entries a CHAT draft may carry.
 *
 * ⚠️ The tool itself has no such cap, and that difference is deliberate: a list
 * she typed herself is hers, while this one arrives from a model and lands in
 * an editor she then has to read. Forty "turns" prefilled from one sentence is
 * not a prefill, it is a mess she has to clear. Her own additions after Save
 * are unaffected.
 */
export const ROUTINE_DRAFT_MAX_ITEMS = 20;

/** How many code points of emoji survive. Enough for a flag or a ZWJ family. */
const ROUTINE_DRAFT_EMOJI_MAX = 8;

/**
 * A hand-off from chat, whether or not its draft survived parsing.
 *
 * ⚠️ THE WRAPPER EXISTS SO `null` CAN MEAN TWO DIFFERENT THINGS. A bare
 * `RoutineDraftFromChat | null` conflated "chat handed over a draft that did not
 * parse" with "she opened the tool herself from the Tools tile", and those want
 * opposite behaviour: the issue says a failed draft opens the EMPTY EDITOR, and
 * the tile must still land on her list. One nullable value cannot say both.
 * Copilot caught that I had shipped the tile behaviour for both.
 */
export interface RoutineHandoff {
  /** null when the [ROUTINE_DRAFT] block was absent or unparseable. */
  draft: RoutineDraftFromChat | null;
}

/** The editor's starting point, in the same shape `draftFromStarter` produces. */
export interface RoutineDraftFromChat {
  name: string;
  emoji: string;
  mode: RoutineMode;
  /** Keyed by JS getDay, "0" = Sunday. Only non-blank days are present. */
  weekday: Record<string, string>;
  rotation: string[];
}

/**
 * Read the draft out of a reply, or null.
 *
 * ⚠️ NULL IS A FIRST-CLASS ANSWER, not an error path. The issue says: if the
 * draft fails to parse, open the EMPTY editor and say nothing. So every
 * rejection below is a deliberate "she gets a blank editor" rather than
 * something to recover from — a half-read draft prefilled with a name and no
 * days is worse than a blank one, because she has to notice what is missing.
 *
 * ⚠️ EVERY FIELD IS UNTRUSTED. This is model output. The lengths come from the
 * same constants the tool and the table enforce, so a draft cannot prefill a
 * value her own editor would refuse to save.
 */
export function parseRoutineDraft(text: unknown): RoutineDraftFromChat | null {
  if (typeof text !== "string" || !text) return null;
  const match = ROUTINE_DRAFT_RE.exec(text);
  if (!match || !match[1]) return null;

  let raw: unknown;
  try {
    raw = JSON.parse(match[1].trim());
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;

  const name = cleanLine(obj.name, ROUTINE_NAME_MAX);
  // A nameless draft would open an editor that cannot save: `name` is NOT NULL
  // on the table and RoutinesTool refuses a blank one with "Give it a name
  // first." Better the blank editor than a prefill that dead-ends.
  if (!name) return null;

  const weekday = weekdayItems(obj.items);
  const rotation = rotationItems(obj.items).slice(0, ROUTINE_DRAFT_MAX_ITEMS);

  // ⚠️ THE SHAPE OF `items` DECIDES THE MODE, AND IT OVERRULES THE LABEL.
  //
  // `items` is self-describing: an array is a rotation, an object keyed by
  // weekday is not. `mode` beside it is redundant, so when the two disagree the
  // shape is the better evidence and the label is just a word the model got
  // wrong.
  //
  // ⚠️ An earlier version only inferred when `mode` was MISSING, which looked
  // equivalent and was not: a draft labelled "weekday" carrying an ARRAY went
  // down the weekday path, `weekdayItems` normalized the array to `{}` (it
  // drops a wrong shape silently rather than throwing), the emptiness check
  // below then rejected the whole draft, and she got a blank editor with her
  // chore list thrown away. One wrong field, all her work gone. A test caught
  // it; the two behaviours differ only on input the model is most likely to
  // produce.
  let mode: RoutineMode;
  if (Array.isArray(obj.items)) mode = "rotation";
  else if (obj.items && typeof obj.items === "object") mode = "weekday";
  else {
    // No usable `items` at all. The label is all there is, and either way the
    // emptiness check below returns null, so this only decides which branch
    // says so.
    mode = normalizeMode(obj.mode) ?? "weekday";
  }

  // A draft with nothing in it IS the empty editor, so say so by returning
  // null instead of claiming a prefill that prefilled nothing.
  if (mode === "rotation" && rotation.length === 0) return null;
  if (mode === "weekday" && Object.keys(weekday).length === 0) return null;

  const emoji = typeof obj.emoji === "string"
    ? Array.from(obj.emoji.trim()).slice(0, ROUTINE_DRAFT_EMOJI_MAX).join("")
    : "";

  return { name, emoji, mode, weekday, rotation };
}

/**
 * Remove the draft block from text she can see.
 *
 * ⚠️ THE ORPHAN PASS IS NOT BELT AND BRACES. A stream cut mid-block leaves an
 * opener with no closer, and the paired regex above cannot match it — so
 * without the second pass the marker and its JSON both reach her as prose.
 * Everything after a lone opener goes with it, for the same reason
 * `TRUNCATE_ON_UNCLOSED` lists TODO_LIST: the tail is machine payload, not
 * something she is missing out on.
 */
export function stripRoutineDraft(text: unknown): string {
  if (typeof text !== "string") return "";
  let out = text.replace(new RegExp(ROUTINE_DRAFT_RE.source, "gi"), "");
  const open = out.search(/\[ROUTINE_DRAFT\]/i);
  if (open >= 0) out = out.slice(0, open);
  out = out.replace(ROUTINE_DRAFT_MARKER_RE, "");
  return stripTrailingPartialMarker(out, "[ROUTINE_DRAFT]");
}

/**
 * Drop a marker that is still arriving: text ending in `...Ready.\n[ROUTINE_DRA`.
 *
 * ⚠️ THE COMPLETE-OPENER PASS ABOVE CANNOT SEE THIS. A chunk boundary can fall
 * INSIDE the marker, and `[ROUTINE_DRA` matches neither the paired regex nor the
 * opener search — so it rendered in her chat until the next chunk arrived.
 * Copilot caught it. One frame, but it is the same leak in a smaller costume,
 * and the whole point of this module is that the plumbing never shows.
 *
 * ⚠️ TRAILING ONLY, and at least three characters. Anchored to the end because
 * a `[RO` in the middle of a sentence is her words, not a marker; three because
 * a bare `[` is a prefix of every tag and would eat a legitimate bracket. False
 * positives are self-healing anyway: display text is rebuilt from the full
 * accumulation on every chunk, so a wrongly hidden `[RO` reappears as soon as
 * the next character proves it was not a marker.
 *
 * ⚠️ THIS FIXES ROUTINES' TWO MARKERS AND NOTHING ELSE. The five older
 * [OPEN_*] tags have the identical gap and are untouched here: widening to
 * them is a change to four other features' streaming behaviour, which does not
 * belong in #1900. Not claiming they are fixed.
 */
export function stripTrailingPartialMarker(text: unknown, marker: string): string {
  if (typeof text !== "string" || !text) return typeof text === "string" ? text : "";
  // Longest prefix first, so `[ROUTINE_DRAFT` is preferred over `[RO`.
  for (let len = marker.length - 1; len >= 3; len--) {
    const prefix = marker.slice(0, len);
    if (text.toUpperCase().endsWith(prefix.toUpperCase())) return text.slice(0, text.length - len);
  }
  return text;
}
